/**
 * Structured Print-Template editor.
 *
 * Lets the operator compose each print type (Receipt, Proforma,
 * Kitchen, Bar, Delivery) as an ordered list of formatted sections.
 * Same mental model as the shop logo editor — the entire ticket
 * becomes data instead of hard-coded Python.
 *
 * The right-hand panel is a live monospaced 42-char paper strip so
 * you see the effect of every change without having to print.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { http } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { confirmDialog, promptDialog } from "@/lib/confirmDialog";
import {
  Printer,
  ArrowUp,
  ArrowDown,
  Trash2,
  Plus,
  Type,
  Minus,
  StretchVertical,
  Scissors,
  Image as ImageIcon,
  ListChecks,
  Save,
  RotateCcw,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Bold,
  Italic,
  Underline,
  GripVertical,
  Send,
} from "lucide-react";

const SECTION_META = {
  text: { label: "Κείμενο", icon: Type },
  items: { label: "Είδη Παραγγελίας", icon: ListChecks },
  separator: { label: "Διαχωριστικό", icon: Minus },
  feed: { label: "Κενή Γραμμή", icon: StretchVertical },
  cut: { label: "Κοπή Χαρτιού", icon: Scissors },
  logo: { label: "Λογότυπο", icon: ImageIcon },
};

const emptySection = (type) => ({
  id: `new-${Math.random().toString(36).slice(2, 10)}`,
  type,
  text: type === "text" ? "" : null,
  line_format: type === "items" ? "{{qty}} x {{name}}" : null,
  comment_format: type === "items" ? "     -  {{comment}}" : null,
  char: type === "separator" ? "-" : null,
  count: type === "feed" ? 1 : type === "separator" ? null : null,
  align: "L",
  bold: false,
  italic: false,
  underline: false,
  double_width: false,
  double_height: false,
});

export default function PrintTemplatesPanel({ scope = "tenant" }) {
  const [kinds, setKinds] = useState([]);
  const [activeKind, setActiveKind] = useState("kitchen");
  const [template, setTemplate] = useState(null);
  const [hasOverride, setHasOverride] = useState(false);
  const [preview, setPreview] = useState({ plaintext: "", segments: [] });
  const [busy, setBusy] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [dragId, setDragId] = useState(null);
  // Sample-source selector — lets the operator preview the template
  // with real DB data (a submitted table / receipt / delivery order)
  // instead of the deterministic mock ctx. Supervisor scope always
  // uses mock ctx because the platform defaults aren't tied to a
  // specific tenant's collections.
  const [sampleSources, setSampleSources] = useState([]);
  const [selectedSource, setSelectedSource] = useState("mock");

  const base = scope === "supervisor"
    ? "/supervisor/print-templates/default"
    : "/print-templates";
  const previewPath = scope === "supervisor"
    ? "/supervisor/print-templates/default/preview"
    : "/print-templates/preview";

  const debounceRef = useRef(null);

  const loadKind = useCallback(async (kind) => {
    setBusy(true);
    try {
      const r = await http.get(`${base}/${kind}`);
      setTemplate(r.data.template);
      setHasOverride(!!r.data.has_override);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Αποτυχία φόρτωσης προτύπου");
    } finally {
      setBusy(false);
    }
  }, [base]);

  // Load kinds list once + initial template.
  useEffect(() => {
    (async () => {
      try {
        const [schema, list] = await Promise.all([
          http.get(`${scope === "supervisor" ? "/print-templates" : "/print-templates"}/schema`).catch(() => null),
          http.get(base).catch(() => null),
        ]);
        const kindsList = list?.data?.items?.map((it) => ({
          value: it.kind, label: it.label, has_override: it.has_override,
        })) || schema?.data?.kinds || [];
        setKinds(kindsList);
      } catch {
        /* non-blocking */
      }
    })();
  }, [base, scope]);

  useEffect(() => { loadKind(activeKind); }, [activeKind, loadKind]);

  // Refresh the sample-source dropdown whenever the operator switches
  // to a different template kind. Supervisor scope skips this (no
  // tenant data to pull from at the platform-defaults level).
  useEffect(() => {
    if (scope === "supervisor") { setSampleSources([]); return; }
    (async () => {
      try {
        const r = await http.get(`/print-templates/sample-sources?kind=${activeKind}`);
        setSampleSources(r?.data?.items || []);
        setSelectedSource("mock");
      } catch {
        setSampleSources([]);
      }
    })();
  }, [activeKind, scope]);

  // Debounced live preview whenever the template OR the sample source
  // changes. When the operator picks a real DB row we forward its
  // (type, id) to the preview endpoint so the backend swaps the mock
  // ctx for authentic data.
  useEffect(() => {
    if (!template) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const body = { template, context: {} };
        if (selectedSource && selectedSource !== "mock") {
          const [t, id] = selectedSource.split(":", 2);
          body.sample_source_type = t;
          body.sample_source_id = id;
        }
        const r = await http.post(previewPath, body);
        setPreview(r.data);
      } catch {
        /* preview is best-effort */
      }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [template, previewPath, selectedSource]);

  const setSection = (id, patch) => {
    setTemplate((t) => ({
      ...t,
      sections: t.sections.map((s) => (s.id === id ? { ...s, ...patch } : s)),
    }));
  };

  const moveSection = (id, dir) => {
    setTemplate((t) => {
      const idx = t.sections.findIndex((s) => s.id === id);
      if (idx < 0) return t;
      const nidx = idx + dir;
      if (nidx < 0 || nidx >= t.sections.length) return t;
      const next = [...t.sections];
      [next[idx], next[nidx]] = [next[nidx], next[idx]];
      return { ...t, sections: next };
    });
  };

  const addSection = (type) => {
    const s = emptySection(type);
    setTemplate((t) => ({ ...t, sections: [...t.sections, s] }));
    setSelectedId(s.id);
  };

  const removeSection = (id) => {
    setTemplate((t) => ({ ...t, sections: t.sections.filter((s) => s.id !== id) }));
    if (selectedId === id) setSelectedId(null);
  };

  const onDragStart = (id) => setDragId(id);
  const onDragEnd = () => setDragId(null);
  const onDrop = (targetId) => {
    if (!dragId || dragId === targetId) return;
    setTemplate((t) => {
      const from = t.sections.findIndex((s) => s.id === dragId);
      const to = t.sections.findIndex((s) => s.id === targetId);
      if (from < 0 || to < 0) return t;
      const next = [...t.sections];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return { ...t, sections: next };
    });
    setDragId(null);
  };

  const save = async () => {
    setBusy(true);
    try {
      await http.put(`${base}/${activeKind}`, template);
      toast.success("Αποθηκεύτηκε");
      setHasOverride(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Αποτυχία");
    } finally {
      setBusy(false);
    }
  };

  const resetToDefault = async () => {
    if (!(await confirmDialog("Επαναφορά προτύπου στις προεπιλογές; Θα χαθούν οι τρέχουσες αλλαγές."))) return;
    setBusy(true);
    try {
      const r = await http.delete(`${base}/${activeKind}`);
      setTemplate(r.data.template);
      setHasOverride(false);
      toast.success("Επαναφορά ολοκληρώθηκε");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Αποτυχία");
    } finally {
      setBusy(false);
    }
  };

  const testPrint = async () => {
    if (scope === "supervisor") {
      toast.info("Το test-print είναι διαθέσιμο μόνο μέσα από τενant instance.");
      return;
    }
    if (!(await confirmDialog(`Δοκιμαστική εκτύπωση του «${kinds.find(k => k.value === activeKind)?.label || activeKind}» στον προεπιλεγμένο εκτυπωτή;`))) return;
    setBusy(true);
    try {
      const r = await http.post(`${base}/${activeKind}/test-print`, {});
      toast.success(`Στάλθηκε στον εκτυπωτή «${r.data.printer_name}»`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Αποτυχία");
    } finally {
      setBusy(false);
    }
  };

  const selectedSection = useMemo(
    () => (template ? template.sections.find((s) => s.id === selectedId) : null),
    [template, selectedId],
  );

  return (
    <Card className="p-4 space-y-4" data-testid="print-templates-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Printer size={18} className="text-blue-600" />
          <h3 className="font-heading text-lg font-semibold">
            Πρότυπα Εκτυπώσεων
          </h3>
          {hasOverride ? (
            <span
              className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-amber-100 text-amber-800"
              data-testid="pt-override-badge"
              data-state="override"
            >
              Προσαρμοσμένο
            </span>
          ) : (
            <span
              className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-slate-100 text-slate-500"
              data-testid="pt-override-badge"
              data-state="default"
            >
              Προεπιλογή
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Select value={activeKind} onValueChange={setActiveKind}>
            <SelectTrigger className="h-9 w-52" data-testid="pt-kind-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {kinds.map((k) => (
                <SelectItem key={k.value} value={k.value}>
                  {k.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={testPrint}
            disabled={busy || !template || scope === "supervisor"}
            variant="outline"
            className="h-9 border-blue-300 text-blue-700 hover:bg-blue-50"
            data-testid="pt-test-print-btn"
            title={scope === "supervisor" ? "Διαθέσιμο μόνο από τον tenant" : "Στέλνει sample στην εκτυπωτή"}
          >
            <Send size={14} className="mr-1.5" /> Δοκιμαστική Εκτύπωση
          </Button>
          <Button
            onClick={save}
            disabled={busy || !template}
            className="bg-emerald-600 hover:bg-emerald-700 h-9"
            data-testid="pt-save-btn"
          >
            <Save size={14} className="mr-1.5" /> Αποθήκευση
          </Button>
          <Button
            onClick={resetToDefault}
            disabled={busy || !template || !hasOverride}
            variant="outline"
            className="h-9"
            data-testid="pt-reset-btn"
          >
            <RotateCcw size={14} className="mr-1.5" /> Επαναφορά
          </Button>
        </div>
      </div>

      {!template ? (
        <div className="text-sm text-slate-500 py-8 text-center">
          Φόρτωση…
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px_1fr] gap-4">
          {/* Sections list */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-wider font-bold text-slate-500">
                Ενότητες
              </div>
            </div>
            <div className="border border-slate-200 rounded-md divide-y divide-slate-200 bg-white max-h-[420px] overflow-y-auto scroll-thin">
              {template.sections.map((s, i) => (
                <SectionRow
                  key={s.id}
                  section={s}
                  index={i}
                  active={selectedId === s.id}
                  dragging={dragId === s.id}
                  onSelect={() => setSelectedId(s.id)}
                  onUp={() => moveSection(s.id, -1)}
                  onDown={() => moveSection(s.id, 1)}
                  onDelete={() => removeSection(s.id)}
                  onDragStart={() => onDragStart(s.id)}
                  onDragEnd={onDragEnd}
                  onDrop={() => onDrop(s.id)}
                  first={i === 0}
                  last={i === template.sections.length - 1}
                />
              ))}
              {template.sections.length === 0 && (
                <div className="text-xs text-slate-500 text-center py-6">
                  Δεν υπάρχουν ενότητες. Πάτησε «+» για να προσθέσεις.
                </div>
              )}
            </div>
            <AddSectionBar onAdd={addSection} />
          </div>

          {/* Selected section editor */}
          <div>
            <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1">
              Επεξεργασία Ενότητας
            </div>
            {selectedSection ? (
              <SectionEditor
                section={selectedSection}
                onChange={(patch) => setSection(selectedSection.id, patch)}
              />
            ) : (
              <div className="border border-dashed border-slate-300 rounded p-4 text-xs text-slate-500 text-center">
                Επίλεξε μια ενότητα από αριστερά για να επεξεργαστείς.
              </div>
            )}
          </div>

          {/* Live preview */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-xs uppercase tracking-wider font-bold text-slate-500">
                Προεπισκόπηση (Live)
              </div>
              {scope !== "supervisor" && (
                <select
                  value={selectedSource}
                  onChange={(e) => setSelectedSource(e.target.value)}
                  data-testid="pt-sample-source-select"
                  className="text-[11px] px-2 py-1 border border-slate-300 rounded bg-white max-w-[260px] truncate"
                  title="Πηγή δεδομένων προεπισκόπησης"
                >
                  <option value="mock">Δείγμα (mock)</option>
                  {sampleSources.length > 0 && (
                    <optgroup label="Πραγματικά δεδομένα">
                      {sampleSources.map((s) => (
                        <option
                          key={`${s.type}:${s.id}`}
                          value={`${s.type}:${s.id}`}
                        >
                          {s.label}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              )}
            </div>
            <div
              className="bg-white border border-slate-300 rounded-md font-mono text-[11px] leading-tight whitespace-pre p-3 min-h-[400px] max-h-[500px] overflow-auto shadow-inner"
              data-testid="pt-live-preview"
              style={{ fontFamily: "ui-monospace, Menlo, monospace" }}
            >
              {preview.plaintext || "(κενό)"}
            </div>
            <div className="text-[10px] text-slate-400 mt-1 text-center">
              Πλάτος χαρτιού: {template.paper_width || 46} χαρακτήρες
            </div>
          </div>
        </div>
      )}

      {/* Placeholders help */}
      <details className="border-t pt-3">
        <summary className="text-xs text-blue-700 cursor-pointer font-bold">
          Διαθέσιμα placeholders
        </summary>
        <div className="text-xs text-slate-600 mt-2 grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 font-mono">
          <span>{"{{business_line_1}}"}</span>
          <span>{"{{business_line_2}}"}</span>
          <span>{"{{business_line_3}}"}</span>
          <span>{"{{business_line_4}}"}</span>
          <span>{"{{business_line_5}}"}</span>
          <span>{"{{business_line_6}}"}</span>
          <span>{"{{business_name}}"}</span>
          <span>{"{{business_address}}"}</span>
          <span>{"{{vat_number}}"}</span>
          <span>{"{{doy}}"}</span>
          <span>{"{{printer_name}}"}</span>
          <span>{"{{table_name}}"}</span>
          <span>{"{{batch_no}}"}</span>
          <span>{"{{order_no}}"}</span>
          <span>{"{{receipt_no}}"}</span>
          <span>{"{{now}}"}</span>
          <span>{"{{operator}}"}</span>
          <span>{"{{customer_name}}"}</span>
          <span>{"{{customer_phone}}"}</span>
          <span>{"{{customer_address}}"}</span>
          <span>{"{{driver_name}}"}</span>
          <span>{"{{grand_total}}"}</span>
          <span>{"{{barcode}}"}</span>
        </div>
        <div className="text-[11px] text-slate-500 mt-2 space-y-1">
          <div>
            <b>Στοιχεία Επιχείρησης</b> — τα 6 πεδία «Στοιχεία Επιχείρησης»
            (Ρυθμίσεις → Στοιχεία) διαβάζονται σειρά-σειρά μέσω{" "}
            <span className="font-mono">{"{{business_line_1}}"}</span>...
            <span className="font-mono">{"{{business_line_6}}"}</span>.
            Ρύθμισε bold / μέγεθος / στοίχιση ανά section.
          </div>
          <div>
            Εντός section «Είδη Παραγγελίας» ισχύουν επιπλέον:{" "}
            <span className="font-mono">{"{{qty}}"}</span>{" "}
            <span className="font-mono">{"{{name}}"}</span>{" "}
            <span className="font-mono">{"{{price}}"}</span>{" "}
            <span className="font-mono">{"{{total}}"}</span>{" "}
            και στο comment_format:{" "}
            <span className="font-mono">{"{{comment}}"}</span>.
          </div>
        </div>
      </details>
    </Card>
  );
}

function SectionRow({
  section, index, active, dragging, onSelect, onUp, onDown, onDelete,
  onDragStart, onDragEnd, onDrop, first, last,
}) {
  const meta = SECTION_META[section.type] || SECTION_META.text;
  const Icon = meta.icon;
  const summary = summarise(section);
  return (
    <div
      className={`flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors ${
        active ? "bg-blue-50 border-l-2 border-l-blue-500" : "hover:bg-slate-50"
      } ${dragging ? "opacity-40" : ""}`}
      onClick={onSelect}
      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
      onDrop={(e) => { e.preventDefault(); onDrop?.(); }}
      data-testid={`pt-section-row-${index}`}
    >
      <span
        draggable
        onDragStart={(e) => { e.stopPropagation(); onDragStart?.(); }}
        onDragEnd={onDragEnd}
        className="w-5 h-5 grid place-items-center text-slate-400 hover:text-slate-700 cursor-grab active:cursor-grabbing"
        title="Σύρε για αλλαγή σειράς"
        data-testid={`pt-drag-${index}`}
      >
        <GripVertical size={14} />
      </span>
      <div className="w-7 h-7 rounded bg-slate-100 grid place-items-center text-slate-600 shrink-0">
        <Icon size={14} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-bold text-slate-800">
          {meta.label}{" "}
          <span className="text-[10px] text-slate-400 font-normal">#{index + 1}</span>
        </div>
        <div className="text-[11px] text-slate-500 truncate">{summary}</div>
      </div>
      <div className="flex items-center gap-0.5 shrink-0">
        <IconAction disabled={first} onClick={(e) => { e.stopPropagation(); onUp(); }}>
          <ArrowUp size={13} />
        </IconAction>
        <IconAction disabled={last} onClick={(e) => { e.stopPropagation(); onDown(); }}>
          <ArrowDown size={13} />
        </IconAction>
        <IconAction danger onClick={(e) => { e.stopPropagation(); onDelete(); }}>
          <Trash2 size={13} />
        </IconAction>
      </div>
    </div>
  );
}

function summarise(s) {
  switch (s.type) {
    case "text":
      return s.text ? s.text.slice(0, 60) : "(κενό)";
    case "items":
      return s.line_format || "(default)";
    case "separator":
      return `${s.char || "-"} × ${s.count || "auto"}`;
    case "feed":
      return `${s.count || 1} κενή/ές γραμμές`;
    case "cut":
      return "ESC/POS κοπή χαρτιού";
    case "logo":
      return "Το λογότυπο του καταστήματος";
    default:
      return s.type;
  }
}

function IconAction({ children, onClick, disabled, danger }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`w-7 h-7 grid place-items-center rounded ${
        disabled
          ? "text-slate-300 cursor-not-allowed"
          : danger
            ? "text-rose-600 hover:bg-rose-50"
            : "text-slate-600 hover:bg-slate-100"
      }`}
    >
      {children}
    </button>
  );
}

function AddSectionBar({ onAdd }) {
  return (
    <div className="flex flex-wrap gap-1 pt-1">
      {Object.entries(SECTION_META).map(([type, meta]) => {
        const Icon = meta.icon;
        return (
          <button
            key={type}
            onClick={() => onAdd(type)}
            data-testid={`pt-add-${type}`}
            className="h-8 px-2 border border-slate-200 rounded text-xs flex items-center gap-1 bg-white hover:bg-blue-50 hover:border-blue-300 text-slate-700"
          >
            <Plus size={12} />
            <Icon size={12} />
            {meta.label}
          </button>
        );
      })}
    </div>
  );
}

function SectionEditor({ section, onChange }) {
  const [textLocal, setTextLocal] = useState(section.text || "");
  useEffect(() => setTextLocal(section.text || ""), [section.id, section.text]);

  return (
    <div className="border border-slate-200 rounded-md p-3 space-y-3 bg-white text-sm">
      {section.type === "text" && (
        <div>
          <Label className="text-xs">Κείμενο (υποστηρίζει {"{{placeholders}}"})</Label>
          <textarea
            value={textLocal}
            onChange={(e) => setTextLocal(e.target.value)}
            onBlur={() => onChange({ text: textLocal })}
            rows={3}
            className="w-full text-sm font-mono border border-slate-200 rounded p-2"
            data-testid="pt-edit-text"
          />
        </div>
      )}

      {section.type === "items" && (
        <>
          <div>
            <Label className="text-xs">Format γραμμής είδους</Label>
            <Input
              value={section.line_format || ""}
              onChange={(e) => onChange({ line_format: e.target.value })}
              className="h-9 font-mono text-xs"
              data-testid="pt-edit-line-format"
            />
          </div>
          <div>
            <Label className="text-xs">Format γραμμής σχολίου</Label>
            <Input
              value={section.comment_format || ""}
              onChange={(e) => onChange({ comment_format: e.target.value })}
              className="h-9 font-mono text-xs"
              data-testid="pt-edit-comment-format"
            />
          </div>
        </>
      )}

      {section.type === "separator" && (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">Χαρακτήρας</Label>
            <Input
              value={section.char || "-"}
              onChange={(e) => onChange({ char: e.target.value.slice(0, 1) })}
              className="h-9 font-mono text-center"
              maxLength={1}
              data-testid="pt-edit-sep-char"
            />
          </div>
          <div>
            <Label className="text-xs">Πλήθος (κενό = auto)</Label>
            <Input
              type="number"
              value={section.count || ""}
              onChange={(e) => onChange({ count: e.target.value ? Number(e.target.value) : null })}
              className="h-9"
              data-testid="pt-edit-sep-count"
            />
          </div>
        </div>
      )}

      {section.type === "feed" && (
        <div>
          <Label className="text-xs">Πλήθος κενών γραμμών</Label>
          <Input
            type="number"
            min={1}
            value={section.count || 1}
            onChange={(e) => onChange({ count: Math.max(1, Number(e.target.value)) })}
            className="h-9"
            data-testid="pt-edit-feed-count"
          />
        </div>
      )}

      {(section.type === "cut" || section.type === "logo") && (
        <div className="text-xs text-slate-500">
          Αυτή η ενότητα δεν χρειάζεται παραμέτρους — απλά τοποθέτησέ την
          στη σωστή θέση.
        </div>
      )}

      {/* Formatting controls (skip for cut/feed) */}
      {section.type !== "cut" && section.type !== "feed" && (
        <div className="pt-2 border-t border-slate-100">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">
            Μορφοποίηση
          </div>
          <div className="flex gap-1 mb-2">
            {[
              { v: "L", icon: AlignLeft, tip: "Αριστερά" },
              { v: "C", icon: AlignCenter, tip: "Κέντρο" },
              { v: "R", icon: AlignRight, tip: "Δεξιά" },
            ].map(({ v, icon: Ic, tip }) => (
              <button
                key={v}
                onClick={() => onChange({ align: v })}
                className={`w-9 h-9 grid place-items-center rounded border ${
                  section.align === v
                    ? "bg-blue-600 border-blue-600 text-white"
                    : "border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}
                title={tip}
                data-testid={`pt-align-${v}`}
              >
                <Ic size={14} />
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-2">
            <FormatToggle label="Έντονα (Bold)" icon={Bold}
              checked={section.bold} onChange={(v) => onChange({ bold: v })} testid="pt-bold" />
            <FormatToggle label="Πλάγια (Italic)" icon={Italic}
              checked={section.italic} onChange={(v) => onChange({ italic: v })} testid="pt-italic" />
            <FormatToggle label="Υπογράμμιση" icon={Underline}
              checked={section.underline} onChange={(v) => onChange({ underline: v })} testid="pt-underline" />
            <FormatToggle label="Διπλό ύψος" icon={StretchVertical}
              checked={section.double_height} onChange={(v) => onChange({ double_height: v })} testid="pt-dh" />
            <FormatToggle label="Διπλό πλάτος" icon={StretchVertical}
              checked={section.double_width} onChange={(v) => onChange({ double_width: v })} testid="pt-dw" />
          </div>
        </div>
      )}
    </div>
  );
}

function FormatToggle({ label, icon: Icon, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
      <Icon size={13} className="text-slate-500" />
      <span className="text-xs">{label}</span>
    </label>
  );
}
